"use client";
import React from "react";
import {
  Box,
  Typography,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const BeforeAfterSection = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const rows = [
    "resumeScreening",
    "firstRoundInterviews",
    "candidateSourcing",
    "talentPoolManagement",
    "interviewQuality",
    "hiringAnalytics",
    "platformSupport",
    "totalTimePerHire",
    "timeToHire",
  ];

  // rows that get the darker "summary" highlight treatment
  const highlightedRows = ["totalTimePerHire", "timeToHire"];

  const cellAlign = isRTL ? "right" : "left";

  return (
    <Box
      sx={{
        py: { xs: 5, sm: 6, md: 8 },
        px: { xs: 1.5, sm: 2 },
        backgroundColor: "#fff",
      }}
    >
      <Container maxWidth="md">
        {/* Badge */}
        <Typography
          sx={{
            fontFamily: "var(--font-outfit)",
            fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
            fontWeight: 600,
            color: "#9CA3AF",
            textAlign: isRTL ? "right" : "left",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          {t("beforeAfter.badge")}
        </Typography>

        {/* Title */}
        <Typography
          sx={{
            fontFamily: "var(--font-plusjakarta)",
            fontWeight: 800,
            fontSize: { xs: "1.5rem", sm: "1.9rem", md: "2.3rem" },
            color: "#0A0A14",
            textAlign: isRTL ? "right" : "left",
            lineHeight: 1.25,
            mb: 1.5,
          }}
        >
          {t("beforeAfter.title")}
        </Typography>

        {/* Description */}
        <Typography
          sx={{
            fontFamily: "var(--font-outfit)",
            fontSize: { xs: "0.8rem", sm: "0.88rem", md: "0.95rem" },
            color: "#6b7280",
            textAlign: isRTL ? "right" : "left",
            maxWidth: { xs: "100%", md: "75%" },
            lineHeight: 1.6,
            mb: { xs: 3, sm: 4 },
          }}
        >
          {t("beforeAfter.description")}
        </Typography>

        {/* Table */}
        <TableContainer
          sx={{
            borderRadius: { xs: "10px", sm: "12px", md: "14px" },
            backgroundColor: "#fff",
            overflow: "hidden",
            boxShadow:
              "0px 4px 32px rgba(37, 11, 57, 0.08), 0px 1px 4px rgba(106, 98, 112, 0.08)",
            direction: isRTL ? "rtl" : "ltr",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f3eeff" }}>
                <TableCell
                  align={cellAlign}
                  sx={{
                    fontFamily: "var(--font-outfit)",
                    fontWeight: 600,
                    fontSize: { xs: "0.65rem", sm: "0.8rem", md: "1.1rem" },
                    color: "#364153",
                    width: "30%",
                    borderBottom: "1px solid #ede9f8",
                    px: { xs: 1, sm: 1.5, md: 2 },
                    py: { xs: 1, sm: 1.2, md: 1.5 },
                  }}
                >
                  {t("beforeAfter.colTask")}
                </TableCell>
                <TableCell
                  align={cellAlign}
                  sx={{
                    fontFamily: "var(--font-outfit)",
                    fontWeight: 600,
                    fontSize: { xs: "0.65rem", sm: "0.8rem", md: "1rem" },
                    color: "#FB2C36",
                    width: "35%",
                    borderBottom: "1px solid #ede9f8",
                    px: { xs: 1, sm: 1.5, md: 2 },
                    py: { xs: 1, sm: 1.2, md: 1.5 },
                  }}
                >
                  {t("beforeAfter.colBefore")}
                </TableCell>
                <TableCell
                  align={cellAlign}
                  sx={{
                    fontFamily: "var(--font-outfit)",
                    fontWeight: 600,
                    fontSize: { xs: "0.65rem", sm: "0.8rem", md: "1rem" },
                    color: "#9810FA",
                    width: "35%",
                    borderBottom: "1px solid #ede9f8",
                    px: { xs: 1, sm: 1.5, md: 2 },
                    py: { xs: 1, sm: 1.2, md: 1.5 },
                  }}
                >
                  {t("beforeAfter.colAfter")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((rowKey, index) => {
                const isHighlighted = highlightedRows.includes(rowKey);
                const isStriped = index % 2 === 1; // rows 2, 4, 6... get the bg

                let bgColor = "#fff";
                if (isHighlighted) {
                  bgColor = "#ede4fb"; // darker purple for last 2 summary rows
                } else if (isStriped) {
                  bgColor = "#f9f7ff"; // light purple stripe
                }

                const borderColor = isHighlighted
                  ? "rgba(152, 16, 250, 0.15)"
                  : "#ede9f8";

                return (
                  <TableRow
                    key={rowKey}
                    sx={{
                      backgroundColor: bgColor,
                      "&:hover": {
                        backgroundColor: isHighlighted
                          ? "#ede4fb"
                          : "#f9f7ff",
                      },
                    }}
                  >
                    <TableCell
                      align={cellAlign}
                      sx={{
                        fontFamily: "var(--font-outfit)",
                        fontWeight: isHighlighted ? 700 : 500,
                        fontSize: {
                          xs: "0.6rem",
                          sm: "0.75rem",
                          md: "0.9rem",
                        },
                        color: "#384054",
                        borderBottom: `1px solid ${borderColor}`,
                        px: { xs: 1, sm: 1.5, md: 2 },
                        py: { xs: 0.8, sm: 1, md: 1.9 },
                      }}
                    >
                      {t(`beforeAfter.rows.${rowKey}.task`)}
                    </TableCell>
                    <TableCell
                      align={cellAlign}
                      sx={{
                        fontFamily: "var(--font-outfit)",
                        fontSize: {
                          xs: "0.6rem",
                          sm: "0.75rem",
                          md: "0.9rem",
                        },
                        color: "#9CA3AF",
                        borderBottom: `1px solid ${borderColor}`,
                        px: { xs: 1, sm: 1.5, md: 2 },
                        py: { xs: 0.8, sm: 1, md: 1.2 },
                      }}
                    >
                      {t(`beforeAfter.rows.${rowKey}.before`)}
                    </TableCell>
                    <TableCell
                      align={cellAlign}
                      sx={{
                        fontFamily: "var(--font-outfit)",
                        fontWeight: 700,
                        fontSize: {
                          xs: "0.6rem",
                          sm: "0.75rem",
                          md: "0.85rem",
                        },
                        color: isHighlighted ? "#9810FA" : "#101828",
                        borderBottom: `1px solid ${borderColor}`,
                        px: { xs: 1, sm: 1.5, md: 2 },
                        py: { xs: 0.8, sm: 1, md: 1.2 },
                      }}
                    >
                      {t(`beforeAfter.rows.${rowKey}.after`)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </Box>
  );
};

export default BeforeAfterSection;